import { SimpleBarChart } from "@carbon/charts-react";
import "@carbon/charts-react/styles.css";
import { useWindowSize } from "usehooks-ts";
import React from "react";
import { Spinner } from "components/common/Spinner";

const truncation = ({ width }) => {
  let threshold = 25,
    numCharacter = 20;
  if (width < 1200) {
    threshold = 20;
    numCharacter = 16;
  }
  return {
    type: "end_line", //"none"
    threshold,
    numCharacter
  };
};

const donoutOptions = [
  {
    group: '2V2N 9KYPM version 1',
    value: 20000
  },
  {
    group: 'L22I P66EP L22I P66EP L22I P66EP',
    value: 65000
  },
  {
    group: 'JQAI 2M4L1',
    value: 75000
  },
  {
    group: 'J9DZ F37AP',
    value: 1200
  },
  {
    group: 'YEL48 Q6XK YEL48',
    value: 10000
  },
  {
    group: 'Misc',
    value: 25000
  }
]
const options = (size) => {
  let height = "350px";
  if (size.width < 900) {
    height = "400px";
  }

  return {
    title: "Invoice Statistics",
    axes: {
      left: {
        mapsTo: "value",
        title: "Count" // Updated to reflect actual value being used for Y-axis
      },
      bottom: {
        scaleType: "labels",
        mapsTo: "group",
        truncation: truncation(size),
        title: "Invoice Categories"
      }
    },
    legend: {
      alignment: "center",
      truncation: { type: "none" }
    },
    height,
    tooltip: {
      customHTML: (model) => {
        if (model && model.length > 0) {
          const data = model[0]; // Extract the first item from the model for the tooltip
          return `<div style="font-size: 12px; padding: 5px;">
                    <div style="display: flex; justify-content: space-between;">
                      <p><strong>X-Value:</strong></p>
                      <p>${data.group}</p>
                    </div>
                    <div style="display: flex; justify-content: space-between;">
                      <p><strong>Y-Value (Count):</strong></p>
                      <p>${data.value}</p>
                    </div>
                    <div style="display: flex; justify-content: space-between;">
                      <p><strong>Group:</strong></p>
                      <p>${data.group}</p>
                    </div>
                    <div style="display: flex; justify-content: space-between;">
                      <p><strong>Percentage:</strong></p>
                      <p>${data.percentage || "N/A"}</p>
                    </div>
                  </div>`;
        } else {
          return "Tooltip data not available";
        }
      }
    }
  };
};

const formatData = (stats) => {
  return [
    {
      group: "Total Invoices",
      value: stats?.total_invoice.count,
      percentage: stats?.total_invoice.percentage
    },
    {
      group: "Rerun Invoices",
      value: stats?.rerun_invoices.count,
      percentage: stats?.rerun_invoices.percentage
    },
    {
      group: "Rerun Success",
      value: stats?.rerun_success.count,
      percentage: stats?.rerun_success.percentage
    },
    {
      group: "Rerun Failed",
      value: stats?.rerun_failed.count,
      percentage: stats?.rerun_failed.percentage
    },
    // /////////////////////////////////////////////
    {
      group: "Rerun Invoices By Method A",
      value: stats?.rerun_invoices_by_method_a.count,
      percentage: stats?.rerun_invoices_by_method_a.percentage
    },
    {
      group: "Rerun Success By Method A",
      value: stats?.rerun_success_by_method_a.count,
      percentage: stats?.rerun_success_by_method_a.percentage
    },
    {
      group: "Rerun Invoices By Method B",
      value: stats?.rerun_invoices_by_method_b.count,
      percentage: stats?.rerun_invoices_by_method_b.percentage
    },
    {
      group: "Rerun Success By Method B",
      value: stats?.rerun_success_by_method_b.count,
      percentage: stats?.rerun_success_by_method_b.percentage
    },
    {
      group: "Detected Invoices",
      value: stats?.detected_invoices.count,
      percentage: stats?.detected_invoices.percentage
    },
    {
      group: "Undetected Invoices",
      value: stats?.undetected_invoices.count,
      percentage: stats?.undetected_invoices.percentage
    },

    {
      group: "Auto Balanced Invoices",
      value: stats?.auto_balanced_invoices.count,
      percentage: stats?.auto_balanced_invoices.percentage
    },
    {
      group: "Auto Balanced Invoices By Method A",
      value: stats?.auto_balanced_invoices_by_method_a.count,
      percentage: stats?.auto_balanced_invoices_by_method_a.percentage
    },
    {
      group: "Auto Balanced Invoices By Method B",
      value: stats?.auto_balanced_invoices_by_method_b.count,
      percentage: stats?.auto_balanced_invoices_by_method_b.percentage
    },
    {
      group: "Manual Balanced Invoices",
      value: stats?.manual_balanced_invoices.count,
      percentage: stats?.manual_balanced_invoices.percentage
    },
    {
      group: "Unbalanced Invoices",
      value: stats?.unbalanced_invoices.count,
      percentage: stats?.unbalanced_invoices.percentage
    },

    {
      group: "Rejected Invoices",
      value: stats?.rejected_invoices.count,
      percentage: stats?.rejected_invoices.percentage
    },
    {
      group: "Accepted Invoices",
      value: stats?.accepted_invoices.count,
      percentage: stats?.accepted_invoices.percentage
    },
    {
      group: "Auto Accepted Invoices",
      value: stats?.auto_accepted_invoices.count,
      percentage: stats?.auto_accepted_invoices.percentage
    },
    {
      group: "Auto Accepted Invoices Based On Vendor Analytics",
      value: stats?.auto_accepted_invoices_based_on_vendor_analytics.count,
      percentage: stats?.auto_accepted_invoices_based_on_vendor_analytics.percentage
    },
    {
      group: "Auto Accepted Invoices Based On Vendor Analytics",
      value: stats?.auto_accepted_invoices_based_on_vendor_analytics.count,
      percentage: stats?.auto_accepted_invoices_based_on_vendor_analytics.percentage
    },
    {
      group: "Auto Accepted Invoices By Method A",
      value: stats?.auto_accepted_invoices_by_method_a.count,
      percentage: stats?.auto_accepted_invoices_by_method_a.percentage
    },
    {
      group: "Auto Accepted Invoices By Method B",
      value: stats?.auto_accepted_invoices_by_method_b.count,
      percentage: stats?.auto_accepted_invoices_by_method_b.percentage
    },

    {
      group: "clickBACON Synced Documents",
      value: stats?.clickbacon_synced_documents.count,
      percentage: stats?.clickbacon_synced_documents.percentage
    },


    {
      group: "Total Invoices By Method A",
      value: stats?.total_invoices_by_method_a.count,
      percentage: stats?.total_invoices_by_method_a.percentage
    },


    {
      group: "Total Invoices By Method B",
      value: stats?.total_invoices_by_method_b.count,
      percentage: stats?.total_invoices_by_method_b.percentage
    },

  ];
};

export const BarChart = ({ stats, isLoading }) => {
  const size = useWindowSize(); // Get window size for responsive behavior
  const data = formatData(stats); // Format the provided statistics data

  return (
    <div className="px-5  mx-auto py-2" style={{ minWidth: "500px",minHeight:"400px" }}>
      {isLoading ? (
        <div className="d-flex justify-content-center align-content-center align-items-center">
          <Spinner />
        </div>
      ) : (
        <SimpleBarChart

          data={data || []}
          options={options(size)}
        ></SimpleBarChart>
      )}
    </div>
  );
};
